import { AuditTrailEmployee } from '../../application/domain/entities/AuditTrailEmployeeInfo';
import {
  AuditTrailEvent,
  AuditTrailSection,
  SectionAccess,
} from '../../application/domain/entities/AuditTrailEvent';
import { AuditLogItem } from '../../application/domain/entities/AuditLogItem';
import { AuditTrailResult } from '../../application/domain/entities/AuditTrailResult';
import {
  ComposedAuditEventDto,
  ComposedAuditSectionDto,
  ComposedAuditTrailDto,
} from './ComposedAuditTrailDto';
import { formatAuditValue } from './formatAuditValue';

const toEvent = (dto: ComposedAuditEventDto): AuditTrailEvent => ({
  timestamp: dto.timestamp,
  changedBy: dto.changedBy ?? null,
  ipAddress: dto.ipAddress ?? null,
  browser: dto.browser ?? null,
  eventHeader: dto.eventHeader,
  changes: dto.changes ?? [],
  ...(dto.preparerGroups ? { preparerGroups: dto.preparerGroups } : {}),
});

const toSection = (dto: ComposedAuditSectionDto): AuditTrailSection => ({
  section: dto.section,
  label: dto.label,
  access: dto.access as SectionAccess,
  events: (dto.events ?? []).map(toEvent),
  ...(dto.message ? { message: dto.message } : {}),
});

/**
 * Maps the server-composed trail into the shapes both audit screens already render.
 *
 * The grouped Report reads `sections`; the flat Log reads `toLogItems`. Deriving both from the SAME
 * response is the fix for the two screens disagreeing — they were previously separate compositions of
 * different endpoints, so there was no arrangement of the UI that could have made them match.
 */
export class ComposedAuditTrailMapper {
  static toResult(dto: ComposedAuditTrailDto): AuditTrailResult {
    return {
      i9FormId: dto.i9FormId,
      employee: dto.employee as unknown as AuditTrailEmployee,
      // Sections the caller cannot read arrive with no events and are kept, not filtered: the screen
      // has to be able to say "restricted" rather than render the same emptiness as "never happened".
      sections: (dto.sections ?? []).map(toSection),
      documents: dto.documents ?? [],
      everifyCase: dto.everifyCase ?? null,
    };
  }

  /**
   * Flatten to the Log screen's row shape: one row per field change, and one for an event that carries
   * none (a signature, a status transition) so those don't disappear from a flat view.
   */
  static toLogItems(dto: ComposedAuditTrailDto): AuditLogItem[] {
    return (dto.events ?? []).flatMap((event) => {
      const base = {
        sectionName: sectionNameOf(dto, event),
        changeType: '',
        timestamp: event.timestamp,
        ipAddress: event.ipAddress ?? '',
        changedBy: event.changedBy ?? '',
      };
      if (!event.changes?.length) {
        return [{ ...base, field: event.eventHeader, newValue: '', changeType: 'EVENT' }];
      }
      return event.changes.map((change) => ({
        ...base,
        field: change.field,
        // Date-shaped values are normalised for display; anything else passes through. A raw ISO
        // timestamp next to a hand-typed value reads as a bug.
        oldValue: formatAuditValue(change.oldValue),
        newValue: formatAuditValue(change.newValue),
        changeType: String(change.action ?? ''),
      }));
    });
  }
}

/** Prefer the server's section heading so both screens label a section identically. */
function sectionNameOf(dto: ComposedAuditTrailDto, event: ComposedAuditEventDto): string {
  return dto.sections?.find((s) => s.section === event.section)?.label ?? event.section;
}
